import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { ChevronRight, PhoneIphone as PhoneIcon } from '@material-ui/icons';
import { unpackToDateTime } from '@campfire/hot-date';
import { FallbackAvatar } from '../../general/user-profile/components/Avatar';
import { AVATAR_SIZE, CommunicationsPerson, InboxReply, personInitials, personName } from './communications-model';

interface InboxReplyRowProps {
  reply: InboxReply;
  candidates?: CommunicationsPerson[];
  onOpen: (reply: InboxReply) => void;
  selected?: boolean;
}

const initialsFor = (candidates?: CommunicationsPerson[]) => {
  if (!candidates || candidates.length !== 1) return null;
  return personInitials(candidates[0]);
};

const attributionFor = (candidates?: CommunicationsPerson[]) => {
  if (!candidates || candidates.length === 0) return null;
  if (candidates.length === 1) return personName(candidates[0]);
  const names = candidates.map(personName);
  const shown = names.slice(0, 2).join(' or ');
  return names.length > 2 ? `Could be ${shown} or ${names.length - 2} others` : `Could be ${shown}`;
};

const receivedLabel = (receivedAt: string) => {
  const received = unpackToDateTime(receivedAt);
  const now = unpackToDateTime(new Date().toISOString());
  const { days } = now.diff(received, 'days');
  if (days < 1) return received.toFormat('h:mm a');
  if (days < 2) return 'Yesterday';
  if (days < 7) return received.toFormat('ccc h:mm a');
  return received.toFormat('d LLL');
};

export const InboxReplyRow = ({ reply, candidates, onOpen, selected }: InboxReplyRowProps) => {
  const attribution = attributionFor(candidates);
  const isAmbiguous = Boolean(candidates && candidates.length > 1);

  return (
    <Box
      display='flex'
      alignItems='center'
      borderBottom='1px solid #EFEFEF'
      padding='12px 16px'
      style={{ cursor: 'pointer', backgroundColor: selected ? '#F5F5F5' : undefined }}
      onClick={() => onOpen(reply)}
    >
      <FallbackAvatar size={AVATAR_SIZE} initials={initialsFor(candidates)} style={{ flexShrink: 0 }} />

      <Box flexGrow={1} minWidth={0} paddingLeft='12px' paddingRight='12px'>
        <Box display='flex' alignItems='baseline' style={{ gap: 8 }}>
          <Typography noWrap style={{ fontSize: 14, fontWeight: 600, color: '#444444' }}>
            {attribution ?? reply.contactNumber}
          </Typography>
          {attribution && (
            <Typography noWrap style={{ fontSize: 12, color: '#646464' }}>
              {reply.contactNumber}
            </Typography>
          )}
        </Box>

        <Typography noWrap style={{ fontSize: 14, color: '#444444', marginTop: 2 }}>
          {reply.body}
        </Typography>

        <Box display='flex' alignItems='center' style={{ gap: 8, marginTop: 6 }}>
          {isAmbiguous && (
            <Box style={{ backgroundColor: '#EFEFEF', borderRadius: 3, padding: '2px 8px' }}>
              <Typography style={{ fontSize: 10, fontWeight: 600, color: '#545454' }}>Unconfirmed sender</Typography>
            </Box>
          )}
          {!reply.inReplyToMessageId && (
            <Box style={{ backgroundColor: '#EFEFEF', borderRadius: 3, padding: '2px 8px' }}>
              <Typography style={{ fontSize: 10, fontWeight: 600, color: '#545454' }}>No matching message</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box display='flex' alignItems='center' style={{ gap: 10, flexShrink: 0 }}>
        <Typography style={{ fontSize: 12, color: '#646464', whiteSpace: 'nowrap' }}>
          {receivedLabel(reply.receivedAt)}
        </Typography>
        <Box
          display='flex'
          alignItems='center'
          style={{ backgroundColor: '#d9d9d9', borderRadius: 3, padding: '2px 6px' }}
        >
          <PhoneIcon style={{ width: 10, height: 10, color: '#545454', marginRight: 4 }} />
          <Typography style={{ fontSize: 10, fontWeight: 600, color: '#545454' }}>SMS</Typography>
        </Box>
        <ChevronRight style={{ color: '#9E9E9E' }} />
      </Box>
    </Box>
  );
};
